import React, { useContext, useState } from "react";
import DataContext from "../Context/DAtaContext";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FaRegCircleXmark } from "react-icons/fa6";
import { AlertTriangle, Search, Truck, Users, X } from "lucide-react";
import FarmerOverlay from "../Components/FarmerOverlay";
import supabase from "./../Config/Supabase";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import UsersContext from "../Context/UsersContext";

/* ─────────────────────────────────────────────────────────────────
   Confirmation Modal
───────────────────────────────────────────────────────────────── */
function ConfirmModal({ count, onConfirm, onCancel, loading }) {
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onCancel}
        />

        {/* Card */}
        <motion.div
          className="relative z-10 w-full max-w-sm overflow-hidden bg-white border border-gray-100 shadow-2xl dark:bg-gray-900 rounded-2xl dark:border-gray-700"
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 28 }}
        >
          <div className="p-6">
            {/* Icon */}
            <div className="flex items-center justify-center mx-auto mb-4 border-2 rounded-full w-14 h-14 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700">
              <AlertTriangle className="w-7 h-7 text-amber-500" />
            </div>

            <h2 className="mb-1 text-lg font-bold text-center text-gray-800 dark:text-gray-100">
              Confirm Bulk Delivery
            </h2>
            <p className="mb-6 text-sm text-center text-gray-500 dark:text-gray-400">
              You are about to mark{" "}
              <span className="font-semibold text-emerald-600">{count}</span>{" "}
              package{count !== 1 ? "s" : ""} as delivered. This action cannot
              be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={onCancel}
                disabled={loading}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="opacity-25"
                      />
                      <path
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                        className="opacity-75"
                      />
                    </svg>
                    Delivering…
                  </>
                ) : (
                  <>
                    <Truck className="w-4 h-4" />
                    Deliver All
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Stat Badge
───────────────────────────────────────────────────────────────── */
function StatBadge({ label, value, active, color, onClick }) {
  const colors = {
    green: {
      active:
        "bg-emerald-600 text-white shadow-emerald-200 dark:shadow-emerald-900 shadow-md",
      inactive:
        "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-emerald-400 hover:text-emerald-600",
    },
    red: {
      active:
        "bg-red-500 text-white shadow-red-200 dark:shadow-red-900 shadow-md",
      inactive:
        "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-red-400 hover:text-red-500",
    },
    amber: {
      active:
        "bg-amber-500 text-white shadow-amber-200 dark:shadow-amber-900 shadow-md",
      inactive:
        "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-amber-400 hover:text-amber-500",
    },
    gray: {
      active:
        "bg-green-600 text-white shadow-gray-300 dark:shadow-gray-900 shadow-md",
      inactive:
        "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-400",
    },
  };

  return (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 ${
        active ? colors[color].active : colors[color].inactive
      }`}
    >
      <span>{label}</span>
      <span
        className={`px-1.5 py-0.5 rounded-md text-xs font-bold ${
          active
            ? "bg-white/25 text-white"
            : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
        }`}
      >
        {value}
      </span>
    </motion.button>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────────── */
function FarmerFinish({ users }) {
  const { vegetableBundle, fetchFarmerBundle } = useContext(DataContext);
  const { Payment, farmerbundle } = useContext(DataContext);
  const { user } = useContext(UsersContext);

  const [bulkLoading, setBulkLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [farmerId, setFarmerId] = useState("");
  const [open, setOpen] = useState(false);
  const [deliveryFilter, setDeliveryFilter] = useState("all");
  const location = useLocation();

  // console.log()

  /* ── Open farmer overlay ── */
  const handleFarmer = (id) => {
    setFarmerId(id);
    setOpen(true);
  };

  /* ── Bulk deliver (agent can only deliver own farmers) ── */
  const handleDeliverAll = async () => {
    try {
      setBulkLoading(true);

      // Safety check
      if (!user?.id) {
        toast.error("User not found");
        return;
      }

      /* ---------------------------------------------------------
       Get farmers registered by this logged-in agent
       assuming farmer.creator_id === user.id
    ---------------------------------------------------------- */
      const myFarmers = users?.filter(
        (farmer) => farmer?.creator_id === user.id || [],
      );

      const myFarmerUuids = myFarmers.map((f) => f.uuid);

      /* ---------------------------------------------------------
        Get ONLY pending + fully paid bundles
        for farmers registered by this agent
      ---------------------------------------------------------- */
      const notDeliveredBundles =
        farmerbundle?.filter((bundle) => {
          // Must belong to this agent's farmers
          if (!myFarmerUuids.includes(bundle?.farmer_id)) {
            return false;
          }

          // Already delivered
          if (bundle?.package_delivered_at !== null) {
            return false;
          }

          // Find bundle/package
          const pkg = vegetableBundle?.find((v) => v.id === bundle.bundle_id);

          if (!pkg) return false;

          // Total payment farmer has made
          const totalPaid = (Payment ?? [])
            .filter((p) => p?.farmer_id === bundle?.farmer_id)
            .reduce((sum, p) => sum + (p?.amount ?? 0), 0);

          // Total cost of all bundles farmer has
          const farmerBundles = (farmerbundle ?? []).filter(
            (fb) => fb?.farmer_id === bundle?.farmer_id,
          );

          const totalProductCost = farmerBundles.reduce((sum, fb) => {
            const veg = vegetableBundle?.find((v) => v.id === fb.bundle_id);

            if (!veg) return sum;

            return sum + fb.amount * veg.price;
          }, 0);

          // Farmer must have completed payment
          return totalPaid >= totalProductCost;
        }) || [];

      if (notDeliveredBundles.length === 0) {
        toast.info("No packages to deliver");
        return;
      }

      const ids = notDeliveredBundles.map((b) => b.id);

      /* ---------------------------------------------------------
       Mark delivered
    ---------------------------------------------------------- */
      const deliveredAt = new Date().toISOString();

      const { error } = await supabase
        .from("farmer_bundle")
        .update({
          package_delivered_at: deliveredAt,
        })
        .in("id", ids);

      if (error) {
        toast.error("Failed to deliver packages");
        console.error(error);
        return;
      }

      /* ---------------------------------------------------------
       Insert delivery logs
    ---------------------------------------------------------- */
      const deliveryRecords = notDeliveredBundles
        .map((bundle) => {
          const pkg = vegetableBundle?.find((p) => p.id === bundle.bundle_id);

          const farmer = users?.find((f) => f.uuid === bundle.farmer_id);

          if (!pkg || !farmer) return null;

          return {
            farmer_id: farmer.id,
            farmer_name: farmer.name,
            phone_number: farmer.phone,
            package_name: pkg.name,
            amount: bundle.amount * pkg.price,
            delivery_date: deliveredAt,
            synced: false,
          };
        })
        .filter(Boolean);

      if (deliveryRecords.length > 0) {
        const { error: insertError } = await supabase
          .from("delivery")
          .insert(deliveryRecords)
          .select();

        if (insertError) {
          console.error("Delivery insert error:", insertError);
        }
      }

      await fetchFarmerBundle();

      toast.success(
        `${notDeliveredBundles.length} package${
          notDeliveredBundles.length !== 1 ? "s" : ""
        } delivered successfully`,
      );
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setBulkLoading(false);
      setShowConfirm(false);
    }
  };

  /* ── Derive farmerPayments ── */
  // FIX: guard against undefined Payment / users
  const farmerPayments = (users ?? [])
    .map((fm) => {
      const userPayments = (Payment ?? []).filter(
        (py) => py?.farmer_id === fm?.uuid,
      );
      const totalAmount = userPayments.reduce(
        (sum, py) => sum + (py?.amount ?? 0),
        0,
      );
      return { farmer_id: fm?.uuid, totalAmount, name: fm?.name, id: fm?.id };
    })
    .filter((f) => f.totalAmount > 0);

  /* ── Derive farmerBd (farmers who have fully paid) ── */
  const farmerBd = farmerPayments
    .map((fm) => {
      const bundles = (farmerbundle ?? []).filter(
        (fb) => fb?.farmer_id === fm.farmer_id,
      );

      // FIX: correctly compute totalProduct = Σ (bundle.amount × vegBundle.price)
      const totalProduct = bundles.reduce((total, bundle) => {
        const vegBundle = (vegetableBundle ?? []).find(
          (v) => v.id === bundle.bundle_id,
        );
        if (!vegBundle) return total;
        return total + bundle.amount * vegBundle.price;
      }, 0);

      const difference = totalProduct - fm.totalAmount;

      // Only include farmers whose total product cost ≤ amount paid (fully / over-paid)
      if (difference <= 0) {
        return {
          id: fm.id,
          name: fm.name,
          farmer_id: fm.farmer_id,
          amountPaid: totalProduct,
        };
      }
      return null;
    })
    .filter(Boolean);

  /* ── Delivery counts ── */
  const getDeliveryCounts = () => {
    const counts = {
      all: farmerBd.length,
      delivered: 0,
      notDelivered: 0,
      partial: 0,
    };

    farmerBd.forEach((user) => {
      const bundle = (farmerbundle ?? []).filter(
        (b) => b?.farmer_id === user?.farmer_id,
      );
      const hasDelivered = bundle.some((b) => b?.package_delivered_at !== null);
      const hasUndelivered = bundle.some(
        (b) => b?.package_delivered_at === null,
      );

      if (hasDelivered && hasUndelivered) counts.partial++;
      else if (hasDelivered) counts.delivered++;
      else if (hasUndelivered) counts.notDelivered++;
    });

    return counts;
  };

  const counts = getDeliveryCounts();

  /* ── Filtered list ── */
  const filteredUsers = farmerBd.filter((user) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      user.name?.toLowerCase().includes(q) || user.id?.toString().includes(q);

    if (!matchesSearch) return false;
    if (deliveryFilter === "all") return true;

    const bundle = (farmerbundle ?? []).filter(
      (b) => b?.farmer_id === user.farmer_id,
    );
    const hasDelivered = bundle.some((b) => b?.package_delivered_at !== null);
    const hasUndelivered = bundle.some((b) => b?.package_delivered_at === null);

    if (deliveryFilter === "delivered") return hasDelivered && !hasUndelivered;
    if (deliveryFilter === "not-delivered")
      return hasUndelivered && !hasDelivered;
    if (deliveryFilter === "partial") return hasDelivered && hasUndelivered;
    return true;
  });

  /* ── Row helper: bundles for a farmer ── */
  const getBundleRows = (farmerUuid) => {
    const bundles = (farmerbundle ?? []).filter(
      (b) => b?.farmer_id === farmerUuid,
    );
    return bundles.map((b) => {
      const veg = (vegetableBundle ?? []).find((v) => v.id === b.bundle_id);
      return {
        name: veg?.name ?? "—",
        delivered: b.package_delivered_at,
        amount: b.amount,
      };
    });
  };

  if (farmerBd.length === 0) return null;

  /* ─── Render ─── */
  return (
    <>
      {/* Confirmation modal */}
      {showConfirm && (
        <ConfirmModal
          count={counts?.notDelivered}
          onConfirm={handleDeliverAll}
          onCancel={() => !bulkLoading && setShowConfirm(false)}
          loading={bulkLoading}
        />
      )}

      <FarmerOverlay FarmerId={farmerId} open={open} setOpen={setOpen} />

      <motion.div
        className="mt-14 mb-36"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        {/* ── Section header ── */}
        <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
              <Users className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-sm font-bold leading-tight text-gray-800 md:text-base dark:text-gray-100">
                Completed Payments
              </h1>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {filteredUsers.length} farmer
                {filteredUsers.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name or ID…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full py-2.5 pl-9 pr-8 text-sm rounded-xl outline-none bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 dark:focus:ring-emerald-900 transition"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ── Filters + Deliver All ── */}
        <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex max-w-lg gap-2 overflow-x-auto scroll-smooth">
            <StatBadge
              label="All"
              value={counts.all}
              active={deliveryFilter === "all"}
              color="gray"
              onClick={() => setDeliveryFilter("all")}
            />
            <StatBadge
              label="Delivered"
              value={counts.delivered}
              active={deliveryFilter === "delivered"}
              color="green"
              onClick={() => setDeliveryFilter("delivered")}
            />
            <StatBadge
              label="Not Delivered"
              value={counts.notDelivered}
              active={deliveryFilter === "not-delivered"}
              color="red"
              onClick={() => setDeliveryFilter("not-delivered")}
            />
            <StatBadge
              label="Partial"
              value={counts.partial}
              active={deliveryFilter === "partial"}
              color="amber"
              onClick={() => setDeliveryFilter("partial")}
            />
          </div>

          {/* FIX: show Deliver All button only when "not-delivered" filter is active AND there are pending packages */}
          {location?.pathname === "/home/farmer" ? (
            <>
              <AnimatePresence>
                {deliveryFilter === "not-delivered" &&
                  counts?.notDelivered > 0 && (
                    <motion.button
                      key="deliver-all-btn"
                      initial={{ opacity: 0, scale: 0.9, x: 10 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.9, x: 10 }}
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 26,
                      }}
                      onClick={() => setShowConfirm(true)}
                      disabled={bulkLoading}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200 dark:shadow-emerald-900 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <Truck className="w-4 h-4" />
                      Deliver All ({counts?.notDelivered})
                    </motion.button>
                  )}
              </AnimatePresence>
            </>
          ) : (
            <></>
          )}
        </div>

        {/* ── Table ── */}
        <div className="overflow-hidden border border-gray-100 rounded dark:border-gray-700">
          <div className="overflow-y-auto max-h-[28rem]">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="text-white bg-green-600">
                  <th className="hidden xl:table-cell px-4 py-3.5 text-left text-xs font-semibold  tracking-wider">
                    No
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold  tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold  tracking-wider">
                    Farmer
                  </th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold  tracking-wider">
                    Amount Paid
                  </th>
                  <th className="hidden md:table-cell px-4 py-3.5 text-left text-xs font-semibold  tracking-wider">
                    Package(s)
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold  tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>

              <motion.tbody
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.35 }}
              >
                <AnimatePresence initial={false}>
                  {filteredUsers?.slice(0, 50)?.map((user, index) => {
                    const rows = getBundleRows(user.farmer_id);

                    return (
                      <motion.tr
                        key={user.id}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{
                          duration: 0.25,
                          delay: 0.2,
                        }}
                        onClick={() => handleFarmer(user.id)}
                        className="transition-colors duration-150 bg-white border-b cursor-pointer group border-gray-50 dark:border-gray-800 dark:bg-gray-900 hover:bg-emerald-50 dark:hover:bg-gray-800"
                      >
                        {/* No */}
                        <td className="hidden xl:table-cell px-4 py-3.5 text-xs text-gray-400 dark:text-gray-500">
                          {index + 1}
                        </td>

                        {/* ID */}
                        <td className="px-4 py-3.5 ">
                          <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs font-mono text-gray-600 dark:text-gray-300 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30 group-hover:text-emerald-700 transition-colors">
                            #{user.id}
                          </span>
                        </td>

                        {/* Name */}
                        <td className="px-4 py-3.5 md:text-sm text-xs text-gray-800 dark:text-gray-100">
                          {user.name}
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3.5 text-center">
                          <span className="text-xs text-gray-700 md:text-sm dark:text-gray-200">
                            {user.amountPaid?.toLocaleString()}
                          </span>
                        </td>

                        {/* Packages */}
                        <td className="hidden md:table-cell px-4 py-3.5">
                          <div className="flex flex-col gap-1">
                            {rows.map((r, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"
                              >
                                {r.name}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col gap-1">
                            {rows.map((r, i) => (
                              <span
                                key={i}
                                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full w-fit ${
                                  r.delivered !== null
                                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                    : "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                                }`}
                              >
                                {r.delivered !== null ? (
                                  <IoMdCheckmarkCircleOutline className="w-3.5 h-3.5" />
                                ) : (
                                  <FaRegCircleXmark className="w-3 h-3" />
                                )}
                                <span className="hidden sm:inline">
                                  {r.delivered !== null
                                    ? "Delivered"
                                    : "Pending"}
                                </span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>

                {filteredUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-16 text-center bg-white dark:bg-gray-900"
                    >
                      <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
                        <Users className="w-10 h-10 opacity-30" />
                        <p className="text-sm font-medium">No farmers found</p>
                        <p className="text-xs">
                          {searchTerm
                            ? "Try a different search term"
                            : "No farmers match this filter"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </motion.tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        {filteredUsers.length > 0 && (
          <p className="mt-3 text-xs text-right text-gray-500 dark:text-gray-500">
            Showing {Math.min(filteredUsers.length, 50)} of {farmerBd.length}{" "}
            farmers
          </p>
        )}
      </motion.div>
    </>
  );
}

export default FarmerFinish;
